import flatMap from 'lodash.flatmap'
import { useMemo } from 'react'

import { getRoutingHubTokens } from '@/features/tokens/tokenCatalogRuntime'
import { useOperativeTokenCatalog } from '@/features/tokens/useOperativeTokenCatalog'
import { Currency, CurrencyAmount, Pair, Token, Trade } from '../sdk'
import { isSwapEnabledForPair, isSwapEnabledForToken } from '../config/marketPolicy'
import { PairState, usePairs } from '../data/Reserves'
import { wrappedCurrency } from '../utils/wrappedCurrency'

import { useAppWeb3 } from './index'

export function useAllCommonPairs(currencyA?: Currency, currencyB?: Currency): Pair[] {
  const { chainId } = useAppWeb3()
  const { entries } = useOperativeTokenCatalog()

  const bases: Token[] = useMemo(
    () => (chainId ? getRoutingHubTokens(chainId, entries) : []),
    [chainId, entries],
  )

  const [tokenA, tokenB] = chainId
    ? [wrappedCurrency(currencyA, chainId), wrappedCurrency(currencyB, chainId)]
    : [undefined, undefined]

  const basePairs: [Token, Token][] = useMemo(
    () =>
      flatMap(bases, (base): [Token, Token][] => bases.map(otherBase => [base, otherBase])).filter(
        ([t0, t1]) => t0.address !== t1.address
      ),
    [bases]
  )

  const allPairCombinations: [Token, Token][] = useMemo(
    () =>
      tokenA && tokenB
        ? [
            // the direct pair
            [tokenA, tokenB],
            // token A against all bases
            ...bases.map((base): [Token, Token] => [tokenA, base]),
            // token B against all bases
            ...bases.map((base): [Token, Token] => [tokenB, base]),
            // each base against all bases
            ...basePairs
          ]
            .filter((tokens): tokens is [Token, Token] => Boolean(tokens[0] && tokens[1]))
            .filter(([t0, t1]) => t0.address !== t1.address)
            .filter(([t0, t1]) => {
              if (!chainId) return true
              return isSwapEnabledForToken(chainId, t0) && isSwapEnabledForToken(chainId, t1)
            })
            .filter(([t0, t1]) => {
              if (!chainId) return true
              return isSwapEnabledForPair(chainId, t0, t1)
            })
        : [],
    [tokenA, tokenB, bases, basePairs, chainId]
  )

  const allPairs = usePairs(allPairCombinations)

  // only pass along valid pairs, non-duplicated pairs
  return useMemo(
    () =>
      Object.values(
        allPairs
          // filter out invalid pairs
          .filter((result): result is [PairState.EXISTS, Pair] => Boolean(result[0] === PairState.EXISTS && result[1]))
          // filter out duplicated pairs
          .reduce<{ [pairAddress: string]: Pair }>((memo, [, curr]) => {
            memo[curr.liquidityToken.address] = memo[curr.liquidityToken.address] ?? curr
            return memo
          }, {})
      ),
    [allPairs]
  )
}

/**
 * Returns the best trade for the exact amount of tokens in to the given token out.
 * `pairCurrencyIn` keeps routing pairs warm before an amount is parsed (presets / Safe max).
 */
export function useTradeExactIn(
  currencyAmountIn?: CurrencyAmount,
  currencyOut?: Currency,
  pairCurrencyIn?: Currency,
): Trade | null {
  const allowedPairs = useAllCommonPairs(pairCurrencyIn ?? currencyAmountIn?.currency, currencyOut)
  const amountKey = currencyAmountIn?.raw.toString()
  const outKey =
    currencyOut instanceof Token ? currencyOut.address : currencyOut ? `native:${currencyOut.symbol}` : undefined

  return useMemo(() => {
    if (currencyAmountIn && currencyOut && allowedPairs.length > 0) {
      return (
        Trade.bestTradeExactIn(allowedPairs, currencyAmountIn, currencyOut, {
          maxHops: 3,
          maxNumResults: 1,
        })[0] ?? null
      )
    }
    return null
    // amountKey/outKey: CurrencyAmount/Currency son refs nuevas cada render en derived swap.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity via amountKey/outKey
  }, [allowedPairs, amountKey, outKey])
}

/**
 * Returns the best trade for the token in to the exact amount of token out.
 * `pairCurrencyOut` keeps routing pairs warm before an amount is parsed.
 */
export function useTradeExactOut(
  currencyIn?: Currency,
  currencyAmountOut?: CurrencyAmount,
  pairCurrencyOut?: Currency,
): Trade | null {
  const allowedPairs = useAllCommonPairs(currencyIn, pairCurrencyOut ?? currencyAmountOut?.currency)
  const amountKey = currencyAmountOut?.raw.toString()
  const inKey =
    currencyIn instanceof Token ? currencyIn.address : currencyIn ? `native:${currencyIn.symbol}` : undefined

  return useMemo(() => {
    if (currencyIn && currencyAmountOut && allowedPairs.length > 0) {
      return (
        Trade.bestTradeExactOut(allowedPairs, currencyIn, currencyAmountOut, {
          maxHops: 3,
          maxNumResults: 1,
        })[0] ?? null
      )
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity via amountKey/inKey
  }, [allowedPairs, amountKey, inKey])
}
